import React, { useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heading, Text } from "@/components/ui/typography";
import { Stack } from "@/components/ui/layout";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

const TestCategories = ({
  cardData = [],
  testCategoriesHeight,
  testCategoriesMinWidth,
  testActionsBorder,
  testActionsBackgroundColor,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Memoized styles applied to all cards
  const testCardTypesStyle = useMemo(() => {
    return {
      height: testCategoriesHeight,
      minWidth: testCategoriesMinWidth,
    };
  }, [testCategoriesHeight, testCategoriesMinWidth]);

  const testCardButtonsStyle = useMemo(() => {
    return {
      border: testActionsBorder,
      backgroundColor: testActionsBackgroundColor,
    };
  }, [testActionsBorder, testActionsBackgroundColor]);

  // Handle dragging/swiping gestures for horizontal scrolling
  const handleDragStart = (e) => {
    const container = containerRef.current;
    container.isDragging = true;
    container.startX = e.pageX || e.touches[0].pageX;
    container.scrollLeftStart = container.scrollLeft;
  };

  const handleDragMove = (e) => {
    const container = containerRef.current;
    if (!container.isDragging) return;
    const currentX = e.pageX || e.touches[0].pageX;
    const distanceMoved = currentX - container.startX;
    container.scrollLeft = container.scrollLeftStart - distanceMoved;
  };

  const handleDragEnd = () => {
    const container = containerRef.current;
    container.isDragging = false;
  };

  return (
    <div
      className="flex gap-1 overflow-x-auto overflow-y-hidden p-5 scroll-snap-type-x-mandatory cursor-grab select-none scrollbar-hide"
      ref={containerRef}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {cardData.map((card, index) => (
        <Card
          key={index}
          className={cn(
            "flex-shrink-0 w-[300px] h-[350px] bg-card border-2 border-primary shadow-md hover:scale-105 transition-transform scroll-snap-align-center",
            "flex flex-col justify-between p-5 text-center"
          )}
          style={testCardTypesStyle}
        >
          <CardContent className="p-0 h-full flex flex-col justify-between">
            <Stack spacing="sm" align="center" className="flex-1">
              <Heading level="h3" className="text-2xl font-semibold text-foreground">
                {card.title}
              </Heading>
              <Text variant="muted" className="text-base mb-4">
                {card.description}
              </Text>
            </Stack>
            <Button
              onClick={() => navigate(card.link)}
              className="w-full mt-auto"
              variant="default"
              style={testCardButtonsStyle}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Get Started
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

TestCategories.propTypes = {
  cardData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  testCategoriesHeight: PropTypes.string,
  testCategoriesMinWidth: PropTypes.string,
  testActionsBorder: PropTypes.string,
  testActionsBackgroundColor: PropTypes.string,
};

export default TestCategories;
